import type { ElementType } from 'react';
import { LayoutDashboard, Building2, BriefcaseBusiness, FileText, CreditCard, Package, Puzzle, Tag, Users, Wrench, ArrowRightLeft, Megaphone, Server, Rocket, BarChart3, ShieldCheck, Settings, Trash2 } from 'lucide-react';
import type { User } from '@/features/auth';

export type NavigationLink = { name: string; href: string; icon: ElementType; superadminOnly?: boolean };
export type NavigationEntry = NavigationLink | { name: string; icon: ElementType; children: NavigationLink[] };

const navigation: NavigationEntry[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Empresas', href: '/companies', icon: Building2 },
  { name: 'Comercial', icon: BriefcaseBusiness, children: [
    { name: 'Faturas', href: '/invoices', icon: FileText },
    { name: 'Planos', href: '/plans', icon: CreditCard },
    { name: 'Produtos', href: '/products', icon: Package },
    { name: 'Cupons', href: '/coupons', icon: Tag },
    // Affiliates are not part of the current commercial rollout; keep their existing data/routes intact.
    { name: 'Módulos', href: '/modules', icon: Puzzle },
  ] },
  { name: 'Usuários', href: '/users', icon: Users },
  { name: 'Ferramentas', icon: Wrench, children: [
    { name: 'Migração de conversas', href: '/migration', icon: ArrowRightLeft },
    { name: 'Comunicados', href: '/announcements', icon: Megaphone, superadminOnly: true },
    { name: 'Exclusões de empresas', href: '/company-deletions', icon: Trash2 },
  ] },
  { name: 'Orya Connector', icon: Server, children: [
    { name: 'Connector Gateways', href: '/connector-gateways', icon: Server },
    { name: 'Releases Sidecar', href: '/sidecar-releases', icon: Rocket },
  ] },
  { name: 'Relatórios', icon: BarChart3, children: [
    { name: 'Consumo', href: '/usage', icon: BarChart3, superadminOnly: true },
    { name: 'Logs de Suporte', href: '/staff-access-logs', icon: ShieldCheck, superadminOnly: true },
  ] },
  { name: 'Configurações', href: '/settings/general', icon: Settings },
];

export function managerNavigation(role: User['role'] | undefined, affiliatesEnabled = false): NavigationEntry[] {
  if (!role) return [];
  if (role === 'SUPPORT') return navigation.filter((item) => item.name === 'Empresas');
  return navigation.flatMap<NavigationEntry>((item) => {
    if (!('children' in item)) return [item];
    const entries: NavigationLink[] = item.name === 'Comercial' && affiliatesEnabled
      ? [...item.children, { name: 'Afiliados', href: '/affiliates', icon: Users }] : item.children;
    const children = entries.filter((child) => !child.superadminOnly || role === 'SUPERADMIN')
      .sort((first, second) => first.name.localeCompare(second.name, 'pt-BR'));
    return children.length ? [{ ...item, children }] : [];
  });
}

export function navigationLinkActive(pathname: string, href: string): boolean {
  if (href.startsWith('/settings')) return pathname.startsWith('/settings/');
  return pathname === href || pathname.startsWith(href + '/');
}
